/**
 * @component ry-calendar
 * @category  form
 * @summary   Date / datetime / time / month / year picker. 1:1 port of Fomantic-UI Calendar. Inline or popup, single / range / multi-select, full keyboard nav, format-token parse + emit, Intl-driven localization, min/max/disabled-dates/marked-dates.
 *
 * Reference: Fomantic-UI Calendar module — all 5 type variants, range pairing
 * via `start-calendar`, popup-vs-inline modes, view drill-down (year → month
 * → day → hour → minute), keyboard navigation, today/now button, weekday
 * header, multi-select via array value. We replace Fomantic's static day/month
 * tables with `Intl.DateTimeFormat` for portable localization (set `locale=`
 * to switch). Where Fomantic uses jQuery + popup-module composition, we use
 * `attachFloating` for positioning and our own portalled `<div>` for the panel.
 *
 * @attribute type                  'date' | 'datetime' | 'time' | 'month' | 'year'   default="date"     Selection type.
 * @attribute value                 string?    ISO 8601 date (`YYYY-MM-DD`) or datetime (`YYYY-MM-DDTHH:mm`); empty = no selection.
 * @attribute name                  string?    Submitted name when inside `<ry-form>` / `<ry-field>`.
 * @attribute min                   string?    ISO date — earliest selectable.
 * @attribute max                   string?    ISO date — latest selectable.
 * @attribute first-day-of-week     number     default=0          0=Sun … 6=Sat.
 * @attribute locale                string?    BCP 47 (e.g. `fr-FR`). Defaults to browser locale. Drives month/day names via Intl.
 * @attribute today                 boolean    default=false      Show Today/Now button in footer.
 * @attribute ampm                  boolean    default=true       12-hour with AM/PM toggle. Set to false for 24-hour.
 * @attribute minute-step           number     default=5          Minutes per cell (5/10/15/20/30).
 * @attribute inline                boolean    default=false      Render inline (no popup). When false, anchors to nearest `<input>` child.
 * @attribute trigger               'click' | 'focus' | 'manual'   default="focus"   How the popup opens.
 * @attribute closable              boolean    default=true       Auto-close popup after selection completes.
 * @attribute disabled              boolean    default=false      Disable the whole control.
 * @attribute disabled-dates        string?    JSON or CSV of ISO dates non-selectable.
 * @attribute disabled-days-of-week string?   CSV (e.g. `0,6`) — weekdays non-selectable.
 * @attribute marked-dates          string?    CSV/JSON of ISO dates rendered with marker indicator.
 * @attribute marked-dates-color    string?    Token color name for the marker (red/blue/green/etc).
 * @attribute disable-year          boolean    default=false      Skip year view in drill-down.
 * @attribute disable-month         boolean    default=false      Skip month view in drill-down.
 * @attribute disable-minute        boolean    default=false      Skip minute view in drill-down (datetime stops at hour).
 * @attribute start-calendar        string?    ID of the start calendar; this calendar becomes the end of a range.
 * @attribute end-calendar          string?    ID of the end calendar; this calendar becomes the start of a range.
 * @attribute range                 boolean    default=false      Single-calendar range mode (click twice = start + end).
 * @attribute multiple              boolean    default=false      Multi-select.
 * @attribute format-input          string?    Token format for the input element when popup mode.
 * @attribute placement             string?    default="bottom-start"   Popup placement (see ry-popover).
 * @attribute initial-date          string?    ISO date — initial view focus when no value set.
 * @attribute start-mode            'year' | 'month' | 'day' | 'hour' | 'minute'?   Initial view mode.
 *
 * @property value                  Date | Date[] | null   Getter/setter. For range: `[start, end]`; multiple: `Date[]`; otherwise single Date.
 * @property startDate              Date | null            Range start (range-mode only).
 * @property endDate                Date | null            Range end (range-mode only).
 * @property minDate                Date | null            Earliest selectable.
 * @property maxDate                Date | null            Latest selectable.
 * @property mode                   'year'|'month'|'day'|'hour'|'minute'   Current view mode.
 *
 * @method setDate(date, updateInput?, fireChange?)   Set the date. Silent by default (no event).
 * @method clear()                                    Clear value + input.
 * @method refresh()                                  Re-render the calendar grid.
 * @method open()                                     Open popup (no-op if inline / already open).
 * @method close()                                    Close popup.
 * @method popup()                                    Force-reposition the popup.
 * @method today()                                    Jump view to today.
 * @method changeMonth(delta)                         ±N months on current focus.
 * @method changeYear(delta)                          ±N years on current focus.
 * @method changeMode(mode)                           Switch view mode.
 *
 * @event ry:beforechange
 *        detail: { value, previous }
 *        fires:  before a user selection commits (`value` is the selection about to commit — Date, Date[], or [start, end]; `previous` the current one) — cell click / Enter / Space, `setDate(d, _, true)`, multi-select toggles, range start/end — before the value, input, or render change. `el.value = x` is silent and does not fire it.
 *        cancelable: preventDefault() blocks the selection; value is unchanged and `ry:change` does not fire
 *
 * @event ry:change
 *        detail: { value, type, mode }
 *        fires:  after a selection commits.
 *
 * @event ry:beforeopen
 *        detail: {}
 *        fires:  at the top of `open()` (popup mode only) — input focus/click trigger or `el.open()` — after the already-open guard, before any state change or portal
 *        cancelable: preventDefault() keeps the popup closed; `ry:open` does not fire
 *
 * @event ry:open
 *        detail: {}
 *        fires:  popup opened.
 *
 * @event ry:beforeclose
 *        detail: {}
 *        fires:  at the top of `close()` (popup mode only) — Escape, outside click, auto-close after selection, or `el.close()` — after the already-closed guard, before any state change
 *        cancelable: preventDefault() keeps the popup open; `ry:close` does not fire
 *
 * @event ry:close
 *        detail: {}
 *        fires:  popup closed.
 *
 * @keyboard ArrowLeft/Right ±1 in current mode. ArrowUp/Down ±row (7 for day, 4 for hour, 3 for year/month/minute). PageUp/Down ±1 month. Shift+PageUp/Down ±1 year. Home/End first/last of row. Enter/Space select. Escape close popup.
 *
 * @state data-ry-state="open"|"closed"  (popup mode only)
 *
 * @content   For popup mode, an `<input>` child is auto-detected as the trigger. Otherwise the calendar renders inline.
 *
 * @dom
 *   ry-calendar[type][data-ry-state="open"|"closed"][inline?]
 *     > {authored input slot — only when popup mode}
 *     > div.ry-calendar__panel  [data-ry-target="panel"]   (portals to body when popup; inline stays here)
 *       > div.ry-calendar__header  [data-ry-target="header"]
 *         > button.ry-calendar__prev
 *         > button.ry-calendar__title
 *         > button.ry-calendar__next
 *       > div.ry-calendar__grid  [data-ry-target="grid"][data-mode="day|month|year|hour|minute"]
 *         > div.ry-calendar__cell  [data-ry-target="cell"][data-value][data-state]
 *       > div.ry-calendar__footer  [data-ry-target="footer"]   (only when [today])
 *         > button.ry-calendar__today
 *
 * @gotcha  Setting `el.value = …` is SILENT (no `ry:change`). User-driven selection fires; programmatic does not. Matches `ry-switch`, `ry-select`, etc.
 *
 * @gotcha  `disabled` here disables the WHOLE control (Fomantic's `.ui.disabled.calendar`). Individual date disablement uses `disabled-dates` or `disabled-days-of-week`. Don't confuse the two.
 *
 * @gotcha  Range linkage via `start-calendar="id"`: this end-calendar's `min` auto-tracks the start's value. Update the start, the end re-renders.
 *
 * @gotcha  `format-input` tokens follow Fomantic: `YYYY MMMM D h:mm A`. Single-quoted text inside is literal: `'Year' YYYY`.
 *
 * @example date-inline
 * ```html
 * <ry-calendar type="date" inline></ry-calendar>
 * ```
 *
 * @example date-popup
 * ```html
 * <ry-field label="Birthday">
 *   <ry-calendar type="date">
 *     <input type="text" name="birthday" placeholder="YYYY-MM-DD">
 *   </ry-calendar>
 * </ry-field>
 * ```
 *
 * @example datetime
 * ```html
 * <ry-calendar type="datetime" inline today></ry-calendar>
 * ```
 *
 * @example time-only
 * ```html
 * <ry-calendar type="time" inline></ry-calendar>
 * ```
 *
 * @example month
 * ```html
 * <ry-calendar type="month" inline></ry-calendar>
 * ```
 *
 * @example year
 * ```html
 * <ry-calendar type="year" inline></ry-calendar>
 * ```
 *
 * @example with-min-max
 * ```html
 * <ry-calendar inline type="date" min="2026-05-01" max="2026-06-30"></ry-calendar>
 * ```
 *
 * @example with-marked-dates
 * ```html
 * <ry-calendar inline type="date"
 *   marked-dates="2026-05-10,2026-05-15,2026-05-20"
 *   marked-dates-color="blue"></ry-calendar>
 * ```
 *
 * @example french-locale
 * ```html
 * <ry-calendar inline type="date" locale="fr-FR" first-day-of-week="1"></ry-calendar>
 * ```
 *
 * @example range-linked
 * ```html
 * <ry-calendar inline id="start" type="date"></ry-calendar>
 * <ry-calendar inline id="end" type="date" start-calendar="start"></ry-calendar>
 * ```
 *
 * @example today-button
 * ```html
 * <ry-calendar inline type="date" today></ry-calendar>
 * ```
 *
 * @tutorial set-value-silent
 * ```js
 * const el = mount('<ry-calendar inline type="date"></ry-calendar>');
 * const fired = [];
 * el.addEventListener('ry:change', () => fired.push(1));
 * el.value = new Date(2026, 4, 15);
 * await expectEqual(fired.length, 0, 'silent programmatic set');
 * ```
 *
 * @tutorial user-click-fires-change
 * ```js
 * const el = mount('<ry-calendar inline type="date" initial-date="2026-05-15"></ry-calendar>');
 * const fired = [];
 * el.addEventListener('ry:change', e => fired.push(e.detail));
 * const cell = el.querySelector('[data-ry-target="cell"][data-value="2026-05-15"]');
 * cell.click();
 * await expectEqual(fired.length, 1);
 * ```
 *
 * @tutorial beforechange-veto
 * ```js
 * // preventDefault() on ry:beforechange blocks the selection — value stays
 * // as it was and ry:change does not fire. Same guard for clicks and
 * // setDate(d, _, true).
 * const el = mount('<ry-calendar inline type="date" initial-date="2026-05-15"></ry-calendar>');
 * const changed = [];
 * el.addEventListener('ry:change', () => changed.push(1));
 * el.addEventListener('ry:beforechange', (e) => e.preventDefault(), { once: true });
 * el.querySelector('[data-ry-target="cell"][data-value="2026-05-15"]').click();
 * await expectEqual(el.value, null, 'vetoed selection leaves value null');
 * await expectEqual(changed.length, 0, 'ry:change did not fire');
 * el.querySelector('[data-ry-target="cell"][data-value="2026-05-15"]').click();
 * await expectEqual(el.value?.getDate(), 15, 'without the veto, the click selects');
 * await expectEqual(changed.length, 1, 'ry:change fired once');
 * ```
 *
 * @tutorial beforeopen-veto
 * ```js
 * // preventDefault() on ry:beforeopen keeps the popup closed.
 * const el = mount('<ry-calendar><input></ry-calendar>');
 * const opened = [];
 * el.addEventListener('ry:open', () => opened.push(1));
 * el.addEventListener('ry:beforeopen', (e) => e.preventDefault(), { once: true });
 * el.open();
 * await expectEqual(el.dataset.ryState, 'closed', 'vetoed open leaves it closed');
 * await expectEqual(opened.length, 0, 'ry:open did not fire');
 * el.open();
 * await expectEqual(el.dataset.ryState, 'open', 'without the veto, open() works');
 * await expectEqual(opened.length, 1, 'ry:open fired once');
 * el.close();
 * ```
 *
 * @tutorial beforeclose-veto
 * ```js
 * // preventDefault() on ry:beforeclose keeps the popup open.
 * const el = mount('<ry-calendar><input></ry-calendar>');
 * el.open();
 * const closed = [];
 * el.addEventListener('ry:close', () => closed.push(1));
 * el.addEventListener('ry:beforeclose', (e) => e.preventDefault(), { once: true });
 * el.close();
 * await expectEqual(el.dataset.ryState, 'open', 'vetoed close leaves it open');
 * await expectEqual(closed.length, 0, 'ry:close did not fire');
 * el.close();
 * await expectEqual(el.dataset.ryState, 'closed', 'without the veto, close() works');
 * await expectEqual(closed.length, 1, 'ry:close fired once');
 * ```
 *
 * @tutorial today-button-jumps
 * ```js
 * const el = mount('<ry-calendar inline today type="date"></ry-calendar>');
 * el.changeMonth(-3);
 * const today = el.querySelector('.ry-calendar__today');
 * today.click();
 * await expectEqual(el.value?.toDateString(), new Date().toDateString());
 * ```
 *
 * @chain ry-field
 * ```js
 * const root = mount(`
 *   <ry-field label="When"><ry-calendar type="date" name="when"><input name="when"></ry-calendar></ry-field>
 * `);
 * const cal = root.querySelector('ry-calendar');
 * cal.setDate(new Date(2026, 0, 1), true);
 * await expectEqual(root.querySelector('input').value.includes('2026'), true);
 * ```
 *
 * @see ry-popover
 * @see ry-field
 * @see ry-form
 */

import { RyElement } from '../core/ry-element.js';
import { attachFloating, type Placement } from '../core/ry-floating.js';

// ───────────────────────── Types ─────────────────────────────────────────

export type CalendarType = 'date' | 'datetime' | 'time' | 'month' | 'year';
export type CalendarMode = 'year' | 'month' | 'day' | 'hour' | 'minute';
type ValueLike = Date | Date[] | [Date | null, Date | null] | null;

interface ChangeDetail {
  value: ValueLike;
  type: CalendarType;
  mode: CalendarMode;
}

// `ry:beforechange` is declared once, globally, in types.ts (BeforeChangeDetail)
// — the before/after pair convention shared by every value component.

// ───────────────────────── Date utils ────────────────────────────────────

const pad2 = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

function isSameDay(a: Date | null, b: Date | null): boolean {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

function isSameMonth(a: Date | null, b: Date | null): boolean {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function addMonths(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(1);
  r.setMonth(r.getMonth() + n);
  // Clamp day to month length
  const max = new Date(r.getFullYear(), r.getMonth() + 1, 0).getDate();
  r.setDate(Math.min(d.getDate(), max));
  return r;
}

function addYears(d: Date, n: number): Date {
  return addMonths(d, n * 12);
}

function dateFromISO(s: string | null | undefined): Date | null {
  if (!s) return null;
  // YYYY-MM-DD or YYYY-MM-DDTHH:mm
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (!m) {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  const [, y, mo, da, h, mi, se] = m;
  return new Date(
    Number(y), Number(mo) - 1, Number(da),
    Number(h ?? 0), Number(mi ?? 0), Number(se ?? 0)
  );
}

function dateToISO(d: Date, includeTime = false): string {
  const base = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  if (!includeTime) return base;
  return `${base}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/**
 * Format a date using Fomantic's token set. Single-quoted segments are literal.
 * Supports: D DD M MM MMM MMMM YYYY Y YY h hh H HH m mm s ss A a S dd ddd dddd w ww
 */
function formatDate(date: Date, format: string, locale: string): string {
  // Pre-split literals
  const out: string[] = [];
  let i = 0;
  while (i < format.length) {
    const c = format[i]!;
    if (c === "'") {
      // literal until next quote
      let j = i + 1;
      while (j < format.length && format[j] !== "'") j++;
      out.push(format.slice(i + 1, j));
      i = j + 1;
      continue;
    }
    // Match longest token
    const tokens = ['YYYY', 'MMMM', 'MMM', 'dddd', 'ddd', 'YY', 'Y', 'MM', 'M', 'DD', 'D', 'dd', 'd', 'HH', 'H', 'hh', 'h', 'mm', 'm', 'ss', 's', 'A', 'a', 'S', 'ww', 'w'];
    let matched = '';
    for (const t of tokens) {
      if (format.startsWith(t, i)) { matched = t; break; }
    }
    if (!matched) {
      out.push(c);
      i++;
      continue;
    }
    out.push(renderToken(date, matched, locale));
    i += matched.length;
  }
  return out.join('');
}

function renderToken(d: Date, token: string, locale: string): string {
  const h24 = d.getHours();
  const h12 = ((h24 + 11) % 12) + 1;
  switch (token) {
    case 'YYYY': case 'Y': return String(d.getFullYear());
    case 'YY': return pad2(d.getFullYear() % 100);
    case 'MMMM': return new Intl.DateTimeFormat(locale, { month: 'long' }).format(d);
    case 'MMM': return new Intl.DateTimeFormat(locale, { month: 'short' }).format(d);
    case 'MM': return pad2(d.getMonth() + 1);
    case 'M': return String(d.getMonth() + 1);
    case 'DD': return pad2(d.getDate());
    case 'D': return String(d.getDate());
    case 'dddd': return new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(d);
    case 'ddd': return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(d);
    case 'dd': return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(d).slice(0, 2);
    case 'd': return String(d.getDay());
    case 'HH': return pad2(h24);
    case 'H': return String(h24);
    case 'hh': return pad2(h12);
    case 'h': return String(h12);
    case 'mm': return pad2(d.getMinutes());
    case 'm': return String(d.getMinutes());
    case 'ss': return pad2(d.getSeconds());
    case 's': return String(d.getSeconds());
    case 'A': return h24 < 12 ? 'AM' : 'PM';
    case 'a': return h24 < 12 ? 'am' : 'pm';
    case 'S': {
      const n = d.getDate();
      const s = n % 10, t = n % 100;
      if (s === 1 && t !== 11) return 'st';
      if (s === 2 && t !== 12) return 'nd';
      if (s === 3 && t !== 13) return 'rd';
      return 'th';
    }
    case 'ww': return pad2(weekOfYear(d));
    case 'w': return String(weekOfYear(d));
    default: return token;
  }
}

function weekOfYear(d: Date): number {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dn = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - dn);
  const ys = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil((((t.getTime() - ys.getTime()) / 86400000) + 1) / 7);
}

/**
 * Parse a string using a token format. Best-effort — falls back to native Date.
 */
function parseDate(text: string, format: string | null, locale: string): Date | null {
  if (!text) return null;
  // Try native first if no format
  if (!format) {
    const d = dateFromISO(text);
    if (d) return d;
    const native = new Date(text);
    return isNaN(native.getTime()) ? null : native;
  }
  // Token-based parse: build regex from format
  // Replace tokens with named groups
  let regex = '';
  let i = 0;
  const groups: string[] = [];
  const tokens = ['YYYY', 'MMMM', 'MMM', 'YY', 'MM', 'M', 'DD', 'D', 'HH', 'H', 'hh', 'h', 'mm', 'm', 'A', 'a'];
  while (i < format.length) {
    if (format[i] === "'") {
      let j = i + 1;
      while (j < format.length && format[j] !== "'") j++;
      regex += format.slice(i + 1, j).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      i = j + 1;
      continue;
    }
    let matched = '';
    for (const t of tokens) {
      if (format.startsWith(t, i)) { matched = t; break; }
    }
    if (!matched) {
      regex += format[i]!.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      i++;
      continue;
    }
    groups.push(matched);
    switch (matched) {
      case 'YYYY': regex += '(\\d{4})'; break;
      case 'YY': regex += '(\\d{2})'; break;
      case 'MMMM': case 'MMM': regex += '(\\S+?)'; break;
      case 'MM': case 'DD': case 'HH': case 'hh': case 'mm': regex += '(\\d{2})'; break;
      case 'M': case 'D': case 'H': case 'h': case 'm': regex += '(\\d{1,2})'; break;
      case 'A': regex += '(AM|PM)'; break;
      case 'a': regex += '(am|pm)'; break;
    }
    i += matched.length;
  }
  const m = new RegExp('^' + regex + '$').exec(text.trim());
  if (!m) {
    const d = new Date(text);
    return isNaN(d.getTime()) ? null : d;
  }
  let year = new Date().getFullYear(), month = 0, day = 1, hour = 0, minute = 0;
  let isPM: boolean | null = null;
  groups.forEach((tok, idx) => {
    const val = m[idx + 1]!;
    switch (tok) {
      case 'YYYY': year = Number(val); break;
      case 'YY': year = Number(val) + (Number(val) < 60 ? 2000 : 1900); break;
      case 'MMMM': case 'MMM': {
        const names = Array.from({ length: 12 }, (_, k) =>
          new Intl.DateTimeFormat(locale, { month: tok === 'MMMM' ? 'long' : 'short' })
            .format(new Date(2020, k, 1)).toLowerCase());
        const idx2 = names.findIndex(n => n === val.toLowerCase());
        if (idx2 >= 0) month = idx2;
        break;
      }
      case 'MM': case 'M': month = Number(val) - 1; break;
      case 'DD': case 'D': day = Number(val); break;
      case 'HH': case 'H': hour = Number(val); break;
      case 'hh': case 'h': hour = Number(val); break;
      case 'mm': case 'm': minute = Number(val); break;
      case 'A': case 'a': isPM = /^p/i.test(val); break;
    }
  });
  if (isPM === true && hour < 12) hour += 12;
  if (isPM === false && hour === 12) hour = 0;
  return new Date(year, month, day, hour, minute);
}

// ───────────────────────── Component ─────────────────────────────────────

export class RyCalendar extends RyElement {
  static observedAttributes = [
    'value', 'min', 'max', 'disabled', 'type',
    'disabled-dates', 'disabled-days-of-week', 'marked-dates',
    'first-day-of-week', 'locale', 'today',
  ] as const;

  // Internal state
  #value: Date | Date[] | [Date | null, Date | null] | null = null;
  #focus: Date = new Date();
  #mode: CalendarMode = 'day';
  #panel: HTMLElement | null = null;
  #input: HTMLInputElement | null = null;
  #floatingRestore: (() => void) | null = null;
  #attachCleanup: (() => void) | null = null;
  #outsideCleanup: (() => void) | null = null;
  #escapeCleanup: (() => void) | null = null;
  #rangeStartPending: boolean = false;
  #linkedStart: RyCalendar | null = null;
  #linkedEnd: RyCalendar | null = null;
  // Linked-calendar redraw avoidance — when start changes, we set our min
  // and refresh. When we then update, don't recurse back into start.
  #suppressLinked = false;

  // ────── Lifecycle ──────────────────────────────────────────────────

  setup(): void {
    // Locate authored input child for popup mode
    const input = this.querySelector('input') as HTMLInputElement | null;
    this.#input = input;

    // Hydrate initial value
    const attrVal = this.getAttribute('value');
    if (attrVal) {
      const parsed = dateFromISO(attrVal);
      if (parsed) this.#value = parsed;
    } else if (input?.value) {
      const parsed = parseDate(input.value, this.getAttribute('format-input'), this.#locale());
      if (parsed) this.#value = parsed;
    }

    const startMode = this.getAttribute('start-mode') as CalendarMode | null;
    this.#mode = startMode ?? this.#defaultMode();
    this.#focus = this.#initialFocus();

    // Build panel
    const panel = document.createElement('div');
    panel.setAttribute('data-ry-target', 'panel');
    panel.className = 'ry-calendar__panel';
    panel.setAttribute('role', 'application');
    this.appendChild(panel);
    this.#panel = panel;

    // ARIA on input
    if (input) {
      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-haspopup', 'dialog');
      input.setAttribute('aria-expanded', 'false');
      // Reflect typed input → value
      this.on(input, 'change', () => {
        const parsed = parseDate(input.value, this.getAttribute('format-input'), this.#locale());
        if (parsed) {
          this.setDate(parsed, false, true);
        } else if (input.value === '') {
          this.clear();
        }
      });
    }

    // Wire up open/close
    if (this.#isPopup()) {
      // Set directly — `this.state = 'closed'` is a no-op since the getter
      // defaults to 'closed' when unset, so no state-change would fire and
      // the attribute would stay missing. We want the attribute present.
      this.dataset.ryState = 'closed';
      panel.setAttribute('data-ry-state', 'closed');
      const trigger = this.getAttribute('trigger') ?? 'focus';
      if (input && trigger === 'focus') {
        this.on(input, 'focus', () => this.open());
      } else if (input && trigger === 'click') {
        this.on(input, 'click', () => this.open());
      }
      this.#outsideCleanup = this.onOutsideClick(() => this.close(), {
        skip: () => this.state !== 'open',
        ignore: [input, panel],
      });
      this.#escapeCleanup = this.onEscape(() => this.close(), {
        skip: () => this.state !== 'open',
      });
    } else {
      panel.setAttribute('data-ry-state', 'open');
    }

    // Hook range linkage
    const startId = this.getAttribute('start-calendar');
    if (startId) {
      const startEl = document.getElementById(startId) as RyCalendar | null;
      if (startEl instanceof RyCalendar) {
        this.#linkedStart = startEl;
        // Tell start about us
        if (!startEl.#linkedEnd) startEl.#linkedEnd = this;
        // Mirror min from start.value
        const startVal = startEl.value as Date | null;
        if (startVal && !this.minDate) this.minDate = startVal;
        startEl.addEventListener('ry:change', () => {
          if (this.#suppressLinked) return;
          const sv = startEl.value as Date | null;
          if (sv instanceof Date) this.minDate = sv;
          this.refresh();
        });
      }
    }
    const endId = this.getAttribute('end-calendar');
    if (endId) {
      const endEl = document.getElementById(endId) as RyCalendar | null;
      if (endEl instanceof RyCalendar) {
        this.#linkedEnd = endEl;
        if (!endEl.#linkedStart) endEl.#linkedStart = this;
        const endVal = endEl.value as Date | null;
        if (endVal && !this.maxDate) this.maxDate = endVal;
        endEl.addEventListener('ry:change', () => {
          if (this.#suppressLinked) return;
          const ev = endEl.value as Date | null;
          if (ev instanceof Date) this.maxDate = ev;
          this.refresh();
        });
      }
    }

    this.#render();
    this.#updateInput();
  }

  teardown(): void {
    this.#attachCleanup?.();
    this.#floatingRestore?.();
    this.#outsideCleanup?.();
    this.#escapeCleanup?.();
  }

  attributeChangedCallback(name: string, oldV: string | null, newV: string | null): void {
    if (oldV === newV) return;
    if (!this.#panel) return; // setup not yet run
    switch (name) {
      case 'value': {
        const parsed = newV ? dateFromISO(newV) : null;
        this.#value = parsed;
        if (parsed) this.#focus = parsed;
        this.#render();
        this.#updateInput();
        break;
      }
      case 'type': {
        this.#mode = this.#defaultMode();
        this.#render();
        break;
      }
      case 'disabled': {
        if (newV != null) {
          this.setAttribute('aria-disabled', 'true');
        } else {
          this.removeAttribute('aria-disabled');
        }
        break;
      }
      default:
        this.#render();
    }
  }

  // ────── Public API ─────────────────────────────────────────────────

  get value(): Date | Date[] | [Date | null, Date | null] | null { return this.#value; }
  set value(v: Date | Date[] | [Date | null, Date | null] | null) {
    this.#value = v;
    if (v instanceof Date) this.#focus = v;
    if (Array.isArray(v) && v[0] instanceof Date) this.#focus = v[0] as Date;
    this.#render();
    this.#updateInput();
  }

  get startDate(): Date | null {
    if (Array.isArray(this.#value)) return (this.#value[0] as Date | null) ?? null;
    return null;
  }
  set startDate(d: Date | null) {
    if (!this.hasAttribute('range')) return;
    const arr = (Array.isArray(this.#value) ? this.#value : [null, null]) as [Date | null, Date | null];
    arr[0] = d;
    this.#value = arr;
    this.#render();
  }

  get endDate(): Date | null {
    if (Array.isArray(this.#value)) return (this.#value[1] as Date | null) ?? null;
    return null;
  }
  set endDate(d: Date | null) {
    if (!this.hasAttribute('range')) return;
    const arr = (Array.isArray(this.#value) ? this.#value : [null, null]) as [Date | null, Date | null];
    arr[1] = d;
    this.#value = arr;
    this.#render();
  }

  get minDate(): Date | null {
    return dateFromISO(this.getAttribute('min'));
  }
  set minDate(d: Date | null) {
    if (d) this.setAttribute('min', dateToISO(d));
    else this.removeAttribute('min');
  }

  get maxDate(): Date | null {
    return dateFromISO(this.getAttribute('max'));
  }
  set maxDate(d: Date | null) {
    if (d) this.setAttribute('max', dateToISO(d));
    else this.removeAttribute('max');
  }

  get mode(): CalendarMode { return this.#mode; }

  setDate(date: Date | null, updateInput = false, fireChange = false): void {
    const previous = this.#value;
    this.#value = date;
    if (date) this.#focus = date;
    if (fireChange) {
      const proceed = this.emit<{ value: ValueLike; previous: ValueLike }>('beforechange', {
        value: date,
        previous,
      });
      if (!proceed) {
        this.#value = previous;
        this.#render();
        return;
      }
    }
    this.#render();
    if (updateInput) this.#updateInput();
    if (fireChange) {
      this.emit<ChangeDetail>('change', {
        value: date,
        type: this.#type(),
        mode: this.#mode,
      });
      // Notify linked partner
      this.#suppressLinked = true;
      this.#linkedEnd?.refresh();
      this.#linkedStart?.refresh();
      this.#suppressLinked = false;
    }
  }

  clear(): void {
    this.#value = null;
    this.#rangeStartPending = false;
    this.#render();
    this.#updateInput();
  }

  refresh(): void {
    this.#render();
  }

  open(): void {
    if (!this.#isPopup()) return;
    if (this.state === 'open') return;
    if (!this.emit('beforeopen')) return;
    this.state = 'open';
    if (!this.#panel) return;
    this.#panel.setAttribute('data-ry-state', 'open');
    this.#panel.classList.add('ry-calendar__panel--popup');
    if (this.#input) this.#input.setAttribute('aria-expanded', 'true');
    // Portal & position
    this.#floatingRestore = this.portalToBody(this.#panel);
    const placement = (this.getAttribute('placement') ?? 'bottom-start') as Placement;
    const ref = this.#input ?? this;
    this.#attachCleanup = attachFloating(ref, this.#panel, { placement, offset: 6 });
    this.emit('open');
  }

  close(): void {
    if (!this.#isPopup()) return;
    if (this.state === 'closed') return;
    if (!this.emit('beforeclose')) return;
    this.state = 'closed';
    if (this.#panel) this.#panel.setAttribute('data-ry-state', 'closed');
    if (this.#input) this.#input.setAttribute('aria-expanded', 'false');
    this.#attachCleanup?.();
    this.#attachCleanup = null;
    this.#floatingRestore?.();
    this.#floatingRestore = null;
    this.emit('close');
  }

  popup(): void {
    // Force a reposition by re-attaching
    if (this.state !== 'open' || !this.#panel) return;
    this.#attachCleanup?.();
    const placement = (this.getAttribute('placement') ?? 'bottom-start') as Placement;
    const ref = this.#input ?? this;
    this.#attachCleanup = attachFloating(ref, this.#panel, { placement, offset: 6 });
  }

  today(): void {
    const t = new Date();
    this.#focus = t;
    this.setDate(t, true, true);
  }

  changeMonth(delta: number): void {
    this.#focus = addMonths(this.#focus, delta);
    this.#render();
  }

  changeYear(delta: number): void {
    this.#focus = addYears(this.#focus, delta);
    this.#render();
  }

  changeMode(m: CalendarMode): void {
    this.#mode = m;
    this.#render();
  }

  // ────── Helpers ────────────────────────────────────────────────────

  #type(): CalendarType {
    const t = (this.getAttribute('type') ?? 'date') as CalendarType;
    return ['date', 'datetime', 'time', 'month', 'year'].includes(t) ? t : 'date';
  }

  #locale(): string {
    return this.getAttribute('locale') ?? (typeof navigator !== 'undefined' ? navigator.language : 'en-US');
  }

  #isPopup(): boolean { return !this.hasAttribute('inline'); }

  #firstDayOfWeek(): number {
    const v = Number(this.getAttribute('first-day-of-week') ?? 0);
    return isNaN(v) ? 0 : v;
  }

  #ampm(): boolean { return this.getAttribute('ampm') !== 'false'; }

  #minuteStep(): number {
    const v = Number(this.getAttribute('minute-step') ?? 5);
    return v > 0 ? v : 5;
  }

  #defaultMode(): CalendarMode {
    const type = this.#type();
    if (type === 'year') return 'year';
    if (type === 'month') return 'month';
    if (type === 'time') return 'hour';
    return 'day';
  }

  #initialFocus(): Date {
    if (this.#value instanceof Date) return this.#value;
    const initial = this.getAttribute('initial-date');
    if (initial) {
      const p = dateFromISO(initial);
      if (p) return p;
    }
    return new Date();
  }

  #disabledDateSet(): Set<string> {
    const raw = this.getAttribute('disabled-dates');
    if (!raw) return new Set();
    try {
      // JSON array or CSV
      const arr = raw.trim().startsWith('[') ? JSON.parse(raw) : raw.split(',');
      return new Set(arr.map((s: string) => {
        const d = dateFromISO(String(s).trim());
        return d ? dateToISO(d) : '';
      }).filter(Boolean));
    } catch {
      return new Set();
    }
  }

  #disabledDows(): Set<number> {
    const raw = this.getAttribute('disabled-days-of-week');
    if (!raw) return new Set();
    return new Set(raw.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n)));
  }

  #markedDateSet(): Set<string> {
    const raw = this.getAttribute('marked-dates');
    if (!raw) return new Set();
    try {
      const arr = raw.trim().startsWith('[') ? JSON.parse(raw) : raw.split(',');
      return new Set(arr.map((s: string) => {
        const d = dateFromISO(String(s).trim());
        return d ? dateToISO(d) : '';
      }).filter(Boolean));
    } catch {
      return new Set();
    }
  }

  #isDateDisabled(d: Date): boolean {
    const min = this.minDate;
    const max = this.maxDate;
    if (min && startOfDay(d) < startOfDay(min)) return true;
    if (max && startOfDay(d) > startOfDay(max)) return true;
    const ds = this.#disabledDateSet();
    if (ds.has(dateToISO(d))) return true;
    const dow = this.#disabledDows();
    if (dow.has(d.getDay())) return true;
    return false;
  }

  // ────── Rendering ──────────────────────────────────────────────────

  #render(): void {
    if (!this.#panel) return;
    this.#panel.innerHTML = '';

    const header = this.#renderHeader();
    if (header) this.#panel.appendChild(header);

    const grid = document.createElement('div');
    grid.className = 'ry-calendar__grid';
    grid.setAttribute('data-ry-target', 'grid');
    grid.setAttribute('data-mode', this.#mode);
    this.#panel.appendChild(grid);

    switch (this.#mode) {
      case 'day': this.#renderDayGrid(grid); break;
      case 'month': this.#renderMonthGrid(grid); break;
      case 'year': this.#renderYearGrid(grid); break;
      case 'hour': this.#renderHourGrid(grid); break;
      case 'minute': this.#renderMinuteGrid(grid); break;
    }

    if (this.hasAttribute('today')) {
      const footer = document.createElement('div');
      footer.className = 'ry-calendar__footer';
      footer.setAttribute('data-ry-target', 'footer');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ry-calendar__today';
      const t = this.#type();
      btn.textContent = (t === 'time' || t === 'datetime') ? 'Now' : 'Today';
      btn.addEventListener('click', () => this.today());
      footer.appendChild(btn);
      this.#panel.appendChild(footer);
    }
  }

  #renderHeader(): HTMLElement | null {
    const header = document.createElement('div');
    header.className = 'ry-calendar__header';
    header.setAttribute('data-ry-target', 'header');

    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'ry-calendar__prev';
    prev.setAttribute('aria-label', 'Previous');
    prev.textContent = '‹';
    prev.addEventListener('click', () => this.#stepHeader(-1));

    const title = document.createElement('button');
    title.type = 'button';
    title.className = 'ry-calendar__title';
    title.setAttribute('aria-live', 'polite');
    title.textContent = this.#headerLabel();
    title.addEventListener('click', () => this.#drillUp());

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'ry-calendar__next';
    next.setAttribute('aria-label', 'Next');
    next.textContent = '›';
    next.addEventListener('click', () => this.#stepHeader(1));

    header.append(prev, title, next);
    return header;
  }

  #headerLabel(): string {
    const f = this.#focus;
    const loc = this.#locale();
    switch (this.#mode) {
      case 'day':
        return new Intl.DateTimeFormat(loc, { month: 'long', year: 'numeric' }).format(f);
      case 'month':
        return String(f.getFullYear());
      case 'year': {
        const start = Math.floor(f.getFullYear() / 10) * 10;
        return `${start} – ${start + 9}`;
      }
      case 'hour': case 'minute':
        return new Intl.DateTimeFormat(loc, { month: 'long', day: 'numeric', year: 'numeric' }).format(f);
    }
  }

  #stepHeader(dir: 1 | -1): void {
    switch (this.#mode) {
      case 'day': this.changeMonth(dir); break;
      case 'month': this.changeYear(dir); break;
      case 'year': this.changeYear(dir * 10); break;
      case 'hour': case 'minute': this.changeYear(0); /* day step */ this.#focus = addDays(this.#focus, dir); this.#render(); break;
    }
  }

  #drillUp(): void {
    if (this.#mode === 'day') this.changeMode(this.hasAttribute('disable-month') ? 'year' : 'month');
    else if (this.#mode === 'month') this.changeMode('year');
    else if (this.#mode === 'hour' || this.#mode === 'minute') this.changeMode('day');
  }

  #renderDayGrid(grid: HTMLElement): void {
    const loc = this.#locale();
    const fdow = this.#firstDayOfWeek();
    const marked = this.#markedDateSet();
    const markColor = this.getAttribute('marked-dates-color') ?? '';

    // Weekday header row
    const dows = document.createElement('div');
    dows.className = 'ry-calendar__dows';
    for (let i = 0; i < 7; i++) {
      const day = (i + fdow) % 7;
      const sample = new Date(2024, 0, day); // Jan 2024: Sun=0..Sat=6 happens to align to getDay
      // safer: use known Sunday
      const refSunday = new Date(2024, 11, 1); // Dec 1 2024 is Sunday
      const dt = addDays(refSunday, day);
      const cell = document.createElement('div');
      cell.className = 'ry-calendar__dow';
      cell.textContent = new Intl.DateTimeFormat(loc, { weekday: 'short' }).format(dt).slice(0, 2);
      dows.appendChild(cell);
    }
    grid.appendChild(dows);

    const cells = document.createElement('div');
    cells.className = 'ry-calendar__cells';
    grid.appendChild(cells);

    // First visible cell — back up from 1st of month to fdow
    const first = new Date(this.#focus.getFullYear(), this.#focus.getMonth(), 1);
    const offset = (first.getDay() - fdow + 7) % 7;
    const start = addDays(first, -offset);
    const today = startOfDay(new Date());

    for (let i = 0; i < 42; i++) {
      const d = addDays(start, i);
      const cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'ry-calendar__cell';
      cell.setAttribute('data-ry-target', 'cell');
      cell.setAttribute('data-value', dateToISO(d));
      cell.setAttribute('role', 'gridcell');
      cell.textContent = String(d.getDate());

      const states: string[] = [];
      if (!isSameMonth(d, this.#focus)) states.push('adjacent');
      if (isSameDay(d, today)) {
        states.push('today');
        cell.setAttribute('aria-current', 'date');
      }
      if (this.#isDateDisabled(d)) {
        states.push('disabled');
        cell.setAttribute('aria-disabled', 'true');
        cell.disabled = true;
      }
      if (this.#isSelected(d)) {
        states.push('selected');
        cell.setAttribute('aria-selected', 'true');
      }
      if (this.#isInRange(d)) states.push('range');
      if (marked.has(dateToISO(d))) {
        states.push('marked');
        if (markColor) cell.setAttribute('data-mark-color', markColor);
      }
      cell.setAttribute('data-state', states.join(' '));
      cell.setAttribute(
        'aria-label',
        new Intl.DateTimeFormat(loc, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }).format(d)
      );

      cell.addEventListener('click', (e) => {
        e.preventDefault();
        if (cell.disabled) return;
        this.#selectDate(d);
      });
      cell.addEventListener('keydown', (e) => this.#onCellKeyDown(e, d));

      cells.appendChild(cell);
    }
  }

  #renderMonthGrid(grid: HTMLElement): void {
    const loc = this.#locale();
    grid.className = 'ry-calendar__grid ry-calendar__grid--months';
    const sel = (this.#value instanceof Date) ? this.#value : null;
    for (let m = 0; m < 12; m++) {
      const d = new Date(this.#focus.getFullYear(), m, 1);
      const cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'ry-calendar__cell ry-calendar__cell--month';
      cell.setAttribute('data-ry-target', 'cell');
      cell.setAttribute('data-value', `${d.getFullYear()}-${pad2(m + 1)}-01`);
      cell.setAttribute('role', 'gridcell');
      cell.textContent = new Intl.DateTimeFormat(loc, { month: 'short' }).format(d);
      const states: string[] = [];
      if (sel && isSameMonth(sel, d)) {
        states.push('selected');
        cell.setAttribute('aria-selected', 'true');
      }
      // bound check: any day of month within bounds?
      const lastDayOfMonth = new Date(d.getFullYear(), m + 1, 0);
      if (this.maxDate && startOfDay(d) > startOfDay(this.maxDate)) {
        states.push('disabled');
        cell.disabled = true;
      } else if (this.minDate && startOfDay(lastDayOfMonth) < startOfDay(this.minDate)) {
        states.push('disabled');
        cell.disabled = true;
      }
      cell.setAttribute('data-state', states.join(' '));
      cell.addEventListener('click', () => {
        if (cell.disabled) return;
        this.#focus = d;
        if (this.#type() === 'month') {
          this.setDate(d, true, true);
          if (this.hasAttribute('closable') && this.#isPopup()) this.close();
          else if (this.getAttribute('closable') !== 'false' && this.#isPopup()) this.close();
        } else {
          this.changeMode('day');
        }
      });
      grid.appendChild(cell);
    }
  }

  #renderYearGrid(grid: HTMLElement): void {
    grid.className = 'ry-calendar__grid ry-calendar__grid--years';
    const startYear = Math.floor(this.#focus.getFullYear() / 10) * 10;
    const sel = (this.#value instanceof Date) ? this.#value : null;
    for (let i = 0; i < 12; i++) {
      const y = startYear - 1 + i; // 12 cells: prev decade tail + 10 + next decade head
      const cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'ry-calendar__cell ry-calendar__cell--year';
      cell.setAttribute('data-ry-target', 'cell');
      cell.setAttribute('data-value', `${y}-01-01`);
      cell.setAttribute('role', 'gridcell');
      cell.textContent = String(y);
      const states: string[] = [];
      if (y < startYear || y >= startYear + 10) states.push('adjacent');
      if (sel && sel.getFullYear() === y) {
        states.push('selected');
        cell.setAttribute('aria-selected', 'true');
      }
      if (this.minDate && y < this.minDate.getFullYear()) { states.push('disabled'); cell.disabled = true; }
      if (this.maxDate && y > this.maxDate.getFullYear()) { states.push('disabled'); cell.disabled = true; }
      cell.setAttribute('data-state', states.join(' '));
      cell.addEventListener('click', () => {
        if (cell.disabled) return;
        this.#focus = new Date(y, this.#focus.getMonth(), 1);
        if (this.#type() === 'year') {
          this.setDate(new Date(y, 0, 1), true, true);
          if (this.getAttribute('closable') !== 'false' && this.#isPopup()) this.close();
        } else if (this.hasAttribute('disable-month')) {
          this.changeMode('day');
        } else {
          this.changeMode('month');
        }
      });
      grid.appendChild(cell);
    }
  }

  #renderHourGrid(grid: HTMLElement): void {
    grid.className = 'ry-calendar__grid ry-calendar__grid--hours';
    const ampm = this.#ampm();
    const sel = (this.#value instanceof Date) ? this.#value : null;
    for (let h = 0; h < 24; h++) {
      const cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'ry-calendar__cell ry-calendar__cell--hour';
      cell.setAttribute('data-ry-target', 'cell');
      cell.setAttribute('data-value', String(h));
      cell.setAttribute('role', 'gridcell');
      if (ampm) {
        const h12 = ((h + 11) % 12) + 1;
        cell.textContent = `${h12}${h < 12 ? 'AM' : 'PM'}`;
      } else {
        cell.textContent = pad2(h);
      }
      const states: string[] = [];
      if (sel && sel.getHours() === h) {
        states.push('selected');
        cell.setAttribute('aria-selected', 'true');
      }
      cell.setAttribute('data-state', states.join(' '));
      cell.addEventListener('click', () => {
        const base = this.#value instanceof Date ? this.#value : this.#focus;
        const d = new Date(base.getFullYear(), base.getMonth(), base.getDate(), h, base.getMinutes());
        this.#focus = d;
        if (this.hasAttribute('disable-minute')) {
          this.setDate(d, true, true);
          if (this.getAttribute('closable') !== 'false' && this.#isPopup()) this.close();
        } else {
          this.#value = d;
          this.changeMode('minute');
        }
      });
      grid.appendChild(cell);
    }
  }

  #renderMinuteGrid(grid: HTMLElement): void {
    grid.className = 'ry-calendar__grid ry-calendar__grid--minutes';
    const step = this.#minuteStep();
    const sel = (this.#value instanceof Date) ? this.#value : null;
    const ampm = this.#ampm();
    for (let m = 0; m < 60; m += step) {
      const cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'ry-calendar__cell ry-calendar__cell--minute';
      cell.setAttribute('data-ry-target', 'cell');
      cell.setAttribute('data-value', String(m));
      cell.setAttribute('role', 'gridcell');
      const hour = sel ? sel.getHours() : this.#focus.getHours();
      if (ampm) {
        const h12 = ((hour + 11) % 12) + 1;
        cell.textContent = `${h12}:${pad2(m)}${hour < 12 ? 'AM' : 'PM'}`;
      } else {
        cell.textContent = `${pad2(hour)}:${pad2(m)}`;
      }
      const states: string[] = [];
      if (sel && sel.getMinutes() === m) {
        states.push('selected');
        cell.setAttribute('aria-selected', 'true');
      }
      cell.setAttribute('data-state', states.join(' '));
      cell.addEventListener('click', () => {
        const base = this.#value instanceof Date ? this.#value : this.#focus;
        const d = new Date(base.getFullYear(), base.getMonth(), base.getDate(), base.getHours(), m);
        this.setDate(d, true, true);
        if (this.getAttribute('closable') !== 'false' && this.#isPopup()) this.close();
      });
      grid.appendChild(cell);
    }
  }

  // ────── Selection state ────────────────────────────────────────────

  #isSelected(d: Date): boolean {
    if (this.#value instanceof Date) return isSameDay(this.#value, d);
    if (Array.isArray(this.#value)) {
      // Range or multiple
      if (this.hasAttribute('range')) {
        const [s, e] = this.#value as [Date | null, Date | null];
        return isSameDay(s, d) || isSameDay(e, d);
      }
      return (this.#value as Date[]).some(x => isSameDay(x, d));
    }
    return false;
  }

  #isInRange(d: Date): boolean {
    if (!this.hasAttribute('range') || !Array.isArray(this.#value)) return false;
    const [s, e] = this.#value as [Date | null, Date | null];
    if (!s || !e) return false;
    const t = startOfDay(d).getTime();
    return t > startOfDay(s).getTime() && t < startOfDay(e).getTime();
  }

  #selectDate(d: Date): void {
    const type = this.#type();

    // Multi-select
    if (this.hasAttribute('multiple')) {
      const arr = (Array.isArray(this.#value) ? [...(this.#value as Date[])] : []) as Date[];
      const idx = arr.findIndex(x => isSameDay(x, d));
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(d);
      if (!this.emit<{ value: ValueLike; previous: ValueLike }>('beforechange', { value: arr, previous: this.#value })) return;
      this.#value = arr;
      this.#render();
      this.#updateInput();
      this.emit<ChangeDetail>('change', { value: arr, type, mode: this.#mode });
      return;
    }

    // Range mode (single calendar)
    if (this.hasAttribute('range')) {
      const cur = (Array.isArray(this.#value) ? this.#value : [null, null]) as [Date | null, Date | null];
      let next: [Date | null, Date | null];
      let nextPending: boolean;
      if (!this.#rangeStartPending && (!cur[0] || cur[1])) {
        // Start fresh
        next = [d, null];
        nextPending = true;
      } else {
        // Set end (ensure order)
        const start = cur[0]!;
        next = d.getTime() < start.getTime() ? [d, start] : [start, d];
        nextPending = false;
      }
      if (!this.emit<{ value: ValueLike; previous: ValueLike }>('beforechange', { value: next, previous: this.#value })) return;
      this.#value = next;
      this.#rangeStartPending = nextPending;
      this.#render();
      this.#updateInput();
      this.emit<ChangeDetail>('change', { value: this.#value, type, mode: this.#mode });
      if (!this.#rangeStartPending && this.getAttribute('closable') !== 'false' && this.#isPopup()) {
        this.close();
      }
      return;
    }

    // Date type — done after day
    if (type === 'date') {
      this.setDate(d, true, true);
      if (this.getAttribute('closable') !== 'false' && this.#isPopup()) this.close();
      return;
    }

    // Datetime — drill to hour
    if (type === 'datetime') {
      this.#focus = d;
      this.#value = d;
      this.changeMode('hour');
      return;
    }
  }

  #onCellKeyDown(e: KeyboardEvent, d: Date): void {
    let nd: Date | null = null;
    switch (e.key) {
      case 'ArrowLeft':  nd = addDays(d, -1); break;
      case 'ArrowRight': nd = addDays(d, 1); break;
      case 'ArrowUp':    nd = addDays(d, -7); break;
      case 'ArrowDown':  nd = addDays(d, 7); break;
      case 'PageUp':     nd = e.shiftKey ? addYears(d, -1) : addMonths(d, -1); break;
      case 'PageDown':   nd = e.shiftKey ? addYears(d, 1) : addMonths(d, 1); break;
      case 'Home': {
        const fdow = this.#firstDayOfWeek();
        const off = (d.getDay() - fdow + 7) % 7;
        nd = addDays(d, -off);
        break;
      }
      case 'End': {
        const fdow = this.#firstDayOfWeek();
        const off = (d.getDay() - fdow + 7) % 7;
        nd = addDays(d, 6 - off);
        break;
      }
      case 'Enter': case ' ':
        e.preventDefault();
        this.#selectDate(d);
        return;
      default: return;
    }
    if (nd) {
      e.preventDefault();
      this.#focus = nd;
      this.#render();
      // Focus the new cell
      const target = this.#panel?.querySelector<HTMLButtonElement>(
        `[data-ry-target="cell"][data-value="${dateToISO(nd)}"]`
      );
      target?.focus();
    }
  }

  // ────── Input sync ─────────────────────────────────────────────────

  #updateInput(): void {
    if (!this.#input) return;
    const fmt = this.getAttribute('format-input') ?? this.#defaultFormat();
    const v = this.#value;
    let s = '';
    if (v instanceof Date) {
      s = formatDate(v, fmt, this.#locale());
    } else if (Array.isArray(v)) {
      if (this.hasAttribute('range')) {
        const [a, b] = v as [Date | null, Date | null];
        s = [a, b].filter(Boolean).map(x => formatDate(x as Date, fmt, this.#locale())).join(' - ');
      } else {
        s = (v as Date[]).map(d => formatDate(d, fmt, this.#locale())).join(', ');
      }
    }
    this.#input.value = s;
  }

  #defaultFormat(): string {
    switch (this.#type()) {
      case 'date': return 'MMMM D, YYYY';
      case 'datetime': return 'MMMM D, YYYY h:mm A';
      case 'time': return 'h:mm A';
      case 'month': return 'MMMM YYYY';
      case 'year': return 'YYYY';
    }
  }
}

// Exposed for tests + consumers
export const _dateUtils = {
  pad2, isSameDay, isSameMonth, addDays, addMonths, addYears,
  dateFromISO, dateToISO, formatDate, parseDate, weekOfYear, startOfDay,
};

customElements.define('ry-calendar', RyCalendar);
